import { useCallback, useEffect, useState } from 'react';

import Typography from '@/components/typography';
import { PARCEL_DETAILS_URL } from '@/constants/urls';
import {
  OpenSearchParcel,
  searchForParcels,
} from '@/endpoints/itasca/parcels/search';
import { useAuth } from '@/providers/auth-provider';

import {
  SearchResult,
  SearchResultGeometry,
  SearchResultType,
} from '../models';

export const useParcelNumberSearch = (search: string) => {
  const { userCanAdvancedSearchParcelNumber } = useAuth();
  const [parcelNumbers, setParcelNumbers] = useState<
    SearchResult<OpenSearchParcel>[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [parcelNumbersSeeMore, setParcelNumbersSeeMore] = useState<number>(0);

  useEffect(() => {
    setParcelNumbers([]);
    setParcelNumbersSeeMore(0);
  }, [search]);

  const fetchParcelNumbers = useCallback(
    async (signal: AbortSignal) => {
      if (!search) return;

      if (!userCanAdvancedSearchParcelNumber) return;

      setLoading(true);
      try {
        const { res, err } = await searchForParcels({
          data: {
            filters: [],
            limit: 5,
            offset: 0,
            queries: [{ apn: search }],
          },
          signal,
        });
        if (err) {
          throw err;
        }

        if (res) {
          setParcelNumbers(
            res.data?.results.splice(0, 5).map((x) => ({
              geometry: x.geometry as SearchResultGeometry,
              id: `parcel-number-${x.apn}-${x.fips_code}`,
              label: `${x.apn}, ${
                x.county_name
              }, ${x.parcel_state.toUpperCase()}`,
              node: (
                <Typography>
                  <span>{x.apn}</span>
                  <br />
                  <span>{`${
                    x.county_name
                  }, ${x.parcel_state.toUpperCase()}`}</span>
                </Typography>
              ),
              original: x,
              type: SearchResultType.PARCEL_NUMBER,
              url: `${PARCEL_DETAILS_URL}/${x.parcel_state.toLowerCase()}/${
                x.county_state_slug
              }/${x.clip}`,
            }))
          );
          setParcelNumbersSeeMore(res.data.total_hits ?? 0);
        }
      } catch (err) {
        return;
      } finally {
        setLoading(false);
      }
    },
    [search, userCanAdvancedSearchParcelNumber]
  );

  useEffect(() => {
    const abortController = new AbortController();

    fetchParcelNumbers(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchParcelNumbers]);

  return {
    loading,
    parcelNumbers,
    parcelNumbersSeeMore,
  };
};
